import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import Groups2Rounded from '@mui/icons-material/Groups2Rounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import PeopleAltRounded from '@mui/icons-material/PeopleAltRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import { fetchJson } from '../../apiBase';
import './DanduSubscriptionsManager.css';

const dateText = (value) => value ? new Date(String(value).slice(0, 10) + 'T12:00:00').toLocaleDateString('tr-TR') : '—';
const money = (value) => Number(value || 0).toLocaleString('tr-TR') + ' TL';
const lower = (value) => String(value || '').toLocaleLowerCase('tr-TR');
const parentNames = (item) => (item.parents || []).map((parent) => parent.name).join(', ');
const messageName = (kind) => ({
  INTRO: 'İlk bilgilendirme', RENEWAL: 'Yenileme',
  REMINDER_1: 'İlk hatırlatma', REMINDER_2: 'İkinci hatırlatma', REMINDER_3: 'Üçüncü hatırlatma',
  PAYMENT_RECEIVED: 'Ödeme alındı bildirimi',
})[kind] || kind;

function paymentStatus(item) {
  if (!item.plan) return { label: 'Veli plan seçmedi', tone: 'neutral' };
  if ((item.periods || []).some((period) => period.status === 'PAYMENT_PENDING' || period.status === 'PAST_DUE')) {
    return { label: 'Ödeme bekleniyor', tone: 'warning' };
  }
  if ((item.periods || []).some((period) => period.status === 'PAID')) {
    return { label: 'Ödendi', tone: 'success' };
  }
  return { label: 'Plan seçildi', tone: 'neutral' };
}

function Metric({ icon, value, label, tone = 'blue' }) {
  return <div className={'dsp-metric dsp-metric--' + tone}>
    <span className="dsp-metric__icon">{icon}</span>
    <span className="dsp-metric__copy"><strong>{value}</strong><span>{label}</span></span>
  </div>;
}

function Insight({ icon, label, value, detail }) {
  return <div className="dsp-insight">
    <span className="dsp-insight__heading">{icon}{label}</span>
    <div><strong>{value}</strong><p>{detail}</p></div>
  </div>;
}

function Status({ label, tone = 'neutral' }) {
  return <span className={'dsp-status dsp-status--' + tone}>{label}</span>;
}

export default function DanduSubscriptionsManager({ schoolId }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [classToRemove, setClassToRemove] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState({});
  const [activeTab, setActiveTab] = useState('students');
  const [classFilter, setClassFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const root = '/admin/dandu-subscriptions/' + encodeURIComponent(schoolId);

  const load = useCallback(async (showLoading = false) => {
    if (!schoolId) { setLoading(false); return; }
    if (showLoading) setLoading(true);
    try {
      const response = await fetchJson(root);
      if (!response.ok) throw new Error('Üyelik kayıtları yüklenemedi.');
      const body = await response.json();
      setItems(body.items || []);
      setSelectedClasses(body.selected_classes || []);
      setSummary(body.summary || {});
      setClassFilter((current) => current === 'all' || (body.selected_classes || []).some((item) => String(item.id) === current) ? current : 'all');
    } catch (requestError) {
      setError(requestError.message || 'Üyelik kayıtları yüklenemedi.');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [root, schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    load(true);
    fetchJson('/admin/classes/' + encodeURIComponent(schoolId)).then(async (response) => {
      if (response.ok) setClasses(await response.json());
    }).catch(() => {});
  }, [schoolId, load]);

  const act = async (path, method = 'POST', body, multipart = false) => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetchJson(path, {
        method,
        ...(multipart ? { body } : body !== undefined ? {
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        } : {}),
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(typeof detail?.detail === 'string' ? detail.detail : 'İşlem tamamlanamadı.');
      }
      await load();
      setSuccess('Kaydedildi.');
      return true;
    } catch (requestError) {
      setError(requestError.message || 'İşlem tamamlanamadı.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const downloadInvoice = async (periodId) => {
    setError('');
    try {
      const response = await fetchJson(root + '/periods/' + periodId + '/invoice-file');
      if (!response.ok) throw new Error('Fatura açılamadı.');
      const url = URL.createObjectURL(await response.blob());
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (requestError) {
      setError(requestError.message || 'Fatura açılamadı.');
    }
  };

  const selected = items.find((item) => item.id === selectedId);
  const possibleClasses = classes.filter((item) => !selectedClasses.some((selectedClass) => Number(selectedClass.id) === Number(item.id)))
    .sort((left, right) => left.name.localeCompare(right.name, 'tr'));
  const pendingTotal = items.reduce((total, item) => total + Number(item.pending_total || 0), 0);
  const paidPeriods = items.reduce((total, item) => total + (item.periods || []).filter((period) => period.status === 'PAID').length, 0);
  const invoicedPeriods = items.reduce((total, item) => total + (item.periods || []).filter((period) => period.invoice_number).length, 0);
  const filteredItems = items.filter((item) => {
    if (classFilter !== 'all' && String(item.class_id) !== classFilter) return false;
    if (statusFilter === 'unplanned' && item.plan) return false;
    if (statusFilter === 'pending' && !['PAYMENT_PENDING', 'PAST_DUE'].some((status) => (item.periods || []).some((period) => period.status === status))) return false;
    if (statusFilter === 'paid' && !(item.periods || []).some((period) => period.status === 'PAID')) return false;
    const query = lower(search.trim());
    return !query || lower(item.student_name).includes(query) || lower(parentNames(item)).includes(query) || lower(item.payment_reference).includes(query);
  }).sort((left, right) => left.student_name.localeCompare(right.student_name, 'tr'));

  const openStudent = (item) => {
    setSelectedId(item.id);
    setNote(item.admin_note || '');
    setError('');
    setSuccess('');
  };

  return <div className="dsp-page"><main className="dsp-main">
    <header className="dsp-heading">
      <div className="dsp-heading__copy">
        <span className="dsp-heading__icon"><AccountBalanceWalletRounded /></span>
        <div><h1>DANDU Veli Ödemeleri</h1><p>Üyelikleri, ödemeleri ve faturaları sınıf bazında izleyin.</p></div>
      </div>
      <Button variant="outlined" startIcon={<RefreshRounded />} onClick={() => load(true)} disabled={loading || busy} aria-label="Ödeme bilgilerini yenile">
        <span className="dsp-refresh-label">Yenile</span>
      </Button>
    </header>

    {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
    {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

    {loading ? <div aria-label="Ödeme bilgileri yükleniyor">
      <Skeleton variant="rounded" height={198} sx={{ mb: 1.5 }} />
      <Box sx={{ display: 'flex', gap: 1.5 }}><Skeleton variant="rounded" height={126} sx={{ flex: 1 }} /><Skeleton variant="rounded" height={126} sx={{ flex: 1 }} /><Skeleton variant="rounded" height={126} sx={{ flex: 1 }} /></Box>
    </div> : <>
      <section className="dsp-hero" aria-label="Ödeme özeti">
        <div>
          <span className="dsp-eyebrow">DANDU üyelikleri</span>
          <h2>{summary.total_students || 0} aktif öğrenci</h2>
          <p>Seçili sınıflardaki öğrencilerin plan, ödeme ve fatura durumları burada bir arada.</p>
          <span className="dsp-hero__badge"><CheckCircleRounded /> {selectedClasses.length} sınıf dahil edildi</span>
        </div>
        <div className="dsp-hero__metrics">
          <Metric icon={<Groups2Rounded />} value={selectedClasses.length} label="dahil edilen sınıf" />
          <Metric icon={<PeopleAltRounded />} value={summary.informed || 0} label="bilgilendirilen öğrenci" tone="green" />
          <Metric icon={<ScheduleRounded />} value={summary.pending_periods || 0} label="bekleyen dönem" tone="orange" />
          <Metric icon={<AccountBalanceWalletRounded />} value={money(pendingTotal)} label="bekleyen toplam" tone="purple" />
        </div>
      </section>

      <section className="dsp-insights" aria-label="Ödeme göstergeleri">
        <Insight icon={<InsightsRounded />} label="Plan dağılımı" value={(summary.monthly || 0) + ' aylık · ' + (summary.annual || 0) + ' yıllık'} detail={(summary.total_students || 0) - (summary.monthly || 0) - (summary.annual || 0) + ' öğrenci henüz plan seçmedi.'} />
        <Insight icon={<CheckCircleRounded />} label="Ödeme durumu" value={(summary.paid_students || 0) + ' öğrenci ödeme yaptı'} detail={(summary.pending_periods || 0) + ' dönem ödeme bekliyor.'} />
        <Insight icon={<ReceiptLongRounded />} label="Faturalar" value={invoicedPeriods + ' / ' + paidPeriods + ' dönem'} detail="Ödenmiş dönemlerden faturası işlenenler." />
      </section>

      <div className="dsp-note"><InfoOutlined /><span>Okul taksitleri ayrı kayıtlardır. WhatsApp ödeme mesajları yalnızca gönderim ayarı ve veli onayı uygunsa gönderilir.</span></div>

      <div className="dsp-tabs" role="tablist" aria-label="DANDU ödeme görünümleri">
        <button type="button" role="tab" aria-selected={activeTab === 'students'} aria-controls="dsp-students" className={activeTab === 'students' ? 'is-active' : ''} onClick={() => setActiveTab('students')}>Öğrenciler</button>
        <button type="button" role="tab" aria-selected={activeTab === 'classes'} aria-controls="dsp-classes" className={activeTab === 'classes' ? 'is-active' : ''} onClick={() => setActiveTab('classes')}>Sınıflar</button>
      </div>

      {activeTab === 'students' && <section id="dsp-students" role="tabpanel" className="dsp-panel" aria-label="Öğrenci ödemeleri">
        <div className="dsp-panel__heading"><div><h2>Öğrenciler</h2><p>Ödeme durumunu incelemek için bir öğrencinin detayını açın.</p></div><Status label={filteredItems.length + ' öğrenci'} /></div>
        <div className="dsp-filters">
          <TextField className="dsp-search" size="small" label="Öğrenci, veli veya açıklama metni ara" value={search} onChange={(event) => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }} />
          <FormControl size="small" className="dsp-filter-select"><InputLabel id="dsp-class-filter-label">Sınıf</InputLabel>
            <Select labelId="dsp-class-filter-label" label="Sınıf" value={classFilter} onChange={(event) => setClassFilter(event.target.value)}>
              <MenuItem value="all">Tüm sınıflar</MenuItem>
              {selectedClasses.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" className="dsp-filter-select"><InputLabel id="dsp-status-filter-label">Durum</InputLabel>
            <Select labelId="dsp-status-filter-label" label="Durum" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="all">Tüm durumlar</MenuItem><MenuItem value="unplanned">Veli plan seçmedi</MenuItem><MenuItem value="pending">Ödeme bekleniyor</MenuItem><MenuItem value="paid">Ödeme yapıldı</MenuItem>
            </Select>
          </FormControl>
        </div>
        {filteredItems.length ? <div className="dsp-table-wrap"><Table size="small" className="dsp-table">
          <TableHead><TableRow><TableCell>Öğrenci / veli</TableCell><TableCell>Sınıf</TableCell><TableCell>Plan</TableCell><TableCell>Durum</TableCell><TableCell align="right">Bekleyen</TableCell><TableCell align="right">Fatura</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>{filteredItems.map((item) => <TableRow key={item.id}>
            <TableCell><span className="dsp-student-name">{item.student_name}</span><span className="dsp-student-parents">{parentNames(item) || 'Veli bağlantısı yok'}</span></TableCell>
            <TableCell>{item.class_name || '—'}</TableCell>
            <TableCell>{item.plan === 'MONTHLY' ? 'Aylık' : item.plan === 'ANNUAL' ? 'Yıllık' : 'Seçilmedi'}</TableCell>
            <TableCell><Status {...paymentStatus(item)} /></TableCell>
            <TableCell align="right">{money(item.pending_total)}</TableCell>
            <TableCell align="right">{(item.periods || []).filter((period) => period.invoice_number).length} / {(item.periods || []).filter((period) => period.status === 'PAID').length}</TableCell>
            <TableCell align="right"><Button size="small" endIcon={<ArrowForwardRounded />} onClick={() => openStudent(item)}>Detay</Button></TableCell>
          </TableRow>)}</TableBody>
        </Table></div> : <div className="dsp-empty"><PeopleAltRounded /><strong>{items.length ? 'Eşleşen öğrenci yok' : 'Henüz öğrenci dahil edilmedi'}</strong><span>{items.length ? 'Arama veya filtreleri değiştirebilirsiniz.' : 'Sınıflar sekmesinden bir sınıf eklediğinizde öğrenciler burada görünür.'}</span></div>}
      </section>}

      {activeTab === 'classes' && <section id="dsp-classes" role="tabpanel" className="dsp-panel" aria-label="Dahil edilen sınıflar">
        <div className="dsp-panel__heading"><div><h2>Dahil edilen sınıflar</h2><p>Bir sınıf seçildiğinde o sınıftaki tüm aktif öğrenciler otomatik olarak dahil edilir.</p></div><Status label={selectedClasses.length + ' sınıf'} /></div>
        <div className="dsp-classes">
          {selectedClasses.length ? <div className="dsp-class-grid">{selectedClasses.map((item) => <div className="dsp-class-card" key={item.id}>
            <span className="dsp-class-card__icon"><SchoolRounded /></span>
            <span className="dsp-class-card__copy"><strong>{item.name}</strong><small>{items.filter((student) => Number(student.class_id) === Number(item.id)).length} öğrenci dahil</small></span>
            <IconButton aria-label={item.name + ' sınıfını çıkar'} size="small" color="error" disabled={busy} onClick={() => setClassToRemove(item)}><CloseRounded fontSize="small" /></IconButton>
          </div>)}</div> : <div className="dsp-empty"><SchoolRounded /><strong>Henüz sınıf eklenmedi</strong><span>Ödeme kapsamına almak istediğiniz sınıfı aşağıdan seçin.</span></div>}
          <div className="dsp-class-add">
            <FormControl size="small"><InputLabel id="dsp-add-class-label">Sınıf ekle</InputLabel>
              <Select labelId="dsp-add-class-label" label="Sınıf ekle" value={classId} onChange={(event) => setClassId(event.target.value)}>
                {possibleClasses.map((item) => <MenuItem key={item.id} value={String(item.id)}>{item.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={<AddRounded />} disabled={!classId || busy} onClick={async () => {
              if (await act(root + '/classes', 'POST', { class_id: Number(classId) })) setClassId('');
            }}>Sınıfı dahil et</Button>
          </div>
          <p className="dsp-class-hint">Sınıf çıkarıldığında öğrencilerin üyelikleri pasifleştirilir. Ödeme ve fatura kayıtları saklanır; sınıf yeniden eklenirse üyelikler etkinleşir.</p>
        </div>
      </section>}
    </>}
  </main>

  <Dialog open={Boolean(classToRemove)} onClose={() => { if (!busy) setClassToRemove(null); }}>
    <DialogTitle>Sınıfı çıkar</DialogTitle>
    <DialogContent><DialogContentText>{classToRemove?.name} sınıfını DANDU ödemelerinden çıkarmak istiyor musunuz? Öğrenciler aktif ödeme listesinden çıkarılır; ödeme ve fatura kayıtları saklanır.</DialogContentText>{error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}</DialogContent>
    <DialogActions><Button disabled={busy} onClick={() => setClassToRemove(null)}>Vazgeç</Button><Button color="error" variant="contained" disabled={busy} onClick={async () => {
      if (await act(root + '/classes/' + encodeURIComponent(classToRemove.id), 'DELETE')) setClassToRemove(null);
    }}>Sınıfı çıkar</Button></DialogActions>
  </Dialog>

  <Drawer anchor="right" open={Boolean(selected)} onClose={() => { if (!busy) setSelectedId(null); }} className="dsp-drawer" PaperProps={{ role: 'dialog', 'aria-label': 'Öğrenci ödeme detayı' }}>
    {selected && <>
      <div className="dsp-drawer__header"><div><span className="dsp-eyebrow">Öğrenci ödeme detayı</span><h2>{selected.student_name}</h2><p>{selected.class_name || 'Sınıf bilinmiyor'} sınıfı · {parentNames(selected) || 'Veli bağlantısı yok'}</p></div><IconButton aria-label="Detayı kapat" onClick={() => setSelectedId(null)} disabled={busy}><CloseRounded /></IconButton></div>
      <div className="dsp-drawer__content">
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        <div className="dsp-drawer__summary">
          <div><span>Ödeme açıklama metni</span><strong>{selected.payment_reference}</strong></div>
          <div><span>Plan</span><strong>{selected.plan === 'MONTHLY' ? 'Aylık' : selected.plan === 'ANNUAL' ? 'Yıllık' : 'Henüz seçilmedi'}</strong></div>
          <div><span>Bekleyen toplam</span><strong>{money(selected.pending_total)}</strong></div>
          <div><span>Sonraki dönem</span><strong>{dateText(selected.next_due_date)}</strong></div>
        </div>
        <section className="dsp-drawer__section"><h3>Üyelik bilgileri</h3><p><strong>Veliler:</strong> {(selected.parents || []).map((parent) => parent.name + ' (' + parent.phone + ')').join(', ') || 'Bağlı veli yok'}</p><p><strong>İlk mesaj:</strong> {dateText(selected.first_message_at)} · <strong>Hatırlatma:</strong> {selected.reminder_count || 0}</p><p><strong>T.C. kimlik numarası:</strong> {selected.invoice_identity_number?.trim() || 'Bilgiler boş'}</p><p><strong>Fatura adresi:</strong> {selected.invoice_address?.trim() || 'Bilgiler boş'}</p></section>
        <section className="dsp-drawer__section"><h3>Ödeme dönemleri</h3>
          {selected.periods?.length ? [...selected.periods].reverse().map((period) => <div className="dsp-period" key={period.id}>
            <div className="dsp-period__top"><strong>{dateText(period.period_start)} · {money(period.amount)}</strong><Status label={period.status === 'PAID' ? 'Ödendi' : period.status === 'CANCELLED' ? 'İptal' : 'Bekleniyor'} tone={period.status === 'PAID' ? 'success' : period.status === 'CANCELLED' ? 'neutral' : 'warning'} /></div>
            {period.payment_claimed_at && <p>Veli ödeme bildirdi: {dateText(period.payment_claimed_at)}</p>}
            {period.invoice_number && <p>Fatura numarası: {period.invoice_number}</p>}
            <div className="dsp-period__actions">
              {period.status !== 'PAID' && period.status !== 'CANCELLED' && <Button size="small" variant="outlined" disabled={busy} onClick={() => act(root + '/periods/' + period.id + '/paid')}>Ödeme alındı</Button>}
              {period.status === 'PAID' && <>
                <TextField size="small" label="Fatura numarası" value={invoiceNumber[period.id] ?? period.invoice_number ?? ''} onChange={(event) => setInvoiceNumber({ ...invoiceNumber, [period.id]: event.target.value })} />
                <Button size="small" disabled={busy || !(invoiceNumber[period.id] ?? period.invoice_number)} onClick={() => act(root + '/periods/' + period.id + '/invoice', 'PUT', { invoice_number: invoiceNumber[period.id] ?? period.invoice_number })}>Fatura kesildi</Button>
                {period.invoice_number && <Button component="label" size="small" disabled={busy}>PDF yükle<input hidden type="file" accept="application/pdf" onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) { const form = new FormData(); form.append('file', file); act(root + '/periods/' + period.id + '/invoice-file', 'POST', form, true); event.target.value = ''; }
                }} /></Button>}
                {period.has_invoice_file && <Button size="small" onClick={() => downloadInvoice(period.id)}>Görüntüle</Button>}
              </>}
            </div>
          </div>) : <p>Henüz ödeme dönemi oluşmadı. Veli plan seçtiğinde burada görünür.</p>}
        </section>
        <section className="dsp-drawer__section"><h3>Yönetici notu</h3><TextField label="Not" multiline minRows={2} fullWidth value={note} onChange={(event) => setNote(event.target.value)} /><Button sx={{ mt: 1 }} variant="outlined" disabled={busy} onClick={() => act(root + '/' + selected.id + '/note', 'PUT', { note })}>Notu kaydet</Button></section>
        <section className="dsp-drawer__section"><h3>Ödeme mesajları</h3>{selected.message_history?.length ? selected.message_history.map((entry, index) => <div className="dsp-message-row" key={index}><span>{messageName(entry.kind)}</span><span>{dateText(entry.sent_at)}</span></div>) : <p>Henüz ödeme mesajı gönderilmedi.</p>}</section>
      </div>
    </>}
  </Drawer>
  </div>;
}
